import { QueryClient } from '@tanstack/vue-query'

import { queryKeys } from '@/shared/api/queryKeys'

/**
 * How long a dashboard card or a report counts as fresh. These are the heaviest reads the app
 * makes — full ledger and shipment aggregates — and none of them changes meaningfully in a minute.
 */
export const AGGREGATE_STALE_TIME = 60_000

/**
 * The application's query client (SPEC.md §10.2).
 *
 * Dashboard and report queries get their own defaults. With the global ones (`staleTime` 0,
 * refetch on focus) every alt-tab re-ran nineteen aggregate queries at once; five people switching
 * windows was about ninety-five heavy requests against a pool of sixty connections (audit P-02).
 * A minute of staleness and no refetch on focus removes that storm; the period selector and the
 * building selector still refetch, because they change the query key.
 *
 * @example
 * app.use(VueQueryPlugin, { queryClient: createAppQueryClient() })
 */
export function createAppQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // Defaults are conservative; each query overrides staleTime per the tiers in SPEC.md §10.2.
        refetchOnWindowFocus: true,
        // §10.4: "queries retry with exponential backoff". Three attempts and a ceiling of 10 s —
        // a warehouse laptop on a flaky connection should recover on its own, but a genuinely
        // unreachable API must reach `isError` fast enough for the banner to appear.
        retry: 3,
        retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10_000),
      },
      mutations: {
        // Never retried automatically: every mutation here moves stock or money, and an
        // `Idempotency-Key` is the only safe way to repeat one (§14 case 11).
        retry: 0,
      },
    },
  })

  for (const key of [queryKeys.dashboard.all(), queryKeys.reports.all()]) {
    client.setQueryDefaults(key, {
      staleTime: AGGREGATE_STALE_TIME,
      refetchOnWindowFocus: false,
    })
  }
  return client
}
